/**
 * Validadores e formatadores de domínio (CPF, CNPJ, usernames).
 *
 * As implementações são puras (sem dependências), espelham as regras do
 * backend (App\Rules\CpfValidation / CnpjValidation) e podem ser usadas em
 * superRefine() de schemas zod ou em validações ad-hoc no UI.
 */

const onlyDigits = (v: string) => (v ?? '').replace(/\D+/g, '')

/** Valida um CPF brasileiro pelo algoritmo dos dígitos verificadores. */
export function isValidCpf(value: string): boolean {
  const d = onlyDigits(value)
  if (d.length !== 11) return false
  // Sequências repetidas (000.000.000-00, 111.111.111-11, ) são inválidas
  if (/^(\d)\1{10}$/.test(d)) return false

  const dv = (slice: string, weightStart: number): number => {
    let sum = 0
    for (let i = 0; i < slice.length; i++) {
      sum += parseInt(slice[i], 10) * (weightStart - i)
    }
    const mod = (sum * 10) % 11
    return mod === 10 ? 0 : mod
  }

  const d1 = dv(d.slice(0, 9), 10)
  if (d1 !== parseInt(d[9], 10)) return false
  const d2 = dv(d.slice(0, 10), 11)
  return d2 === parseInt(d[10], 10)
}

/** Valida um CNPJ brasileiro pelo algoritmo dos dígitos verificadores. */
export function isValidCnpj(value: string): boolean {
  const d = onlyDigits(value)
  if (d.length !== 14) return false
  if (/^(\d)\1{13}$/.test(d)) return false

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

  const dv = (slice: string, weights: number[]): number => {
    let sum = 0
    for (let i = 0; i < slice.length; i++) {
      sum += parseInt(slice[i], 10) * weights[i]
    }
    const mod = sum % 11
    return mod < 2 ? 0 : 11 - mod
  }

  const d1 = dv(d.slice(0, 12), weights1)
  if (d1 !== parseInt(d[12], 10)) return false
  const d2 = dv(d.slice(0, 13), weights2)
  return d2 === parseInt(d[13], 10)
}

/**
 * Normaliza um texto para comparação fuzzy: lowercase + remove acentos +
 * remove espaços extras. Útil para casar nomes vindos de APIs externas
 * (em CAIXA ALTA, sem acentos) com registros do banco (formatados).
 *
 * Exemplo:  normalizeForMatch("SAO PAULO") === normalizeForMatch("São Paulo")
 */
export function normalizeForMatch(s: string | null | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Normaliza um nome de usuário ao digitar:
 *  - tudo lowercase
 *  - remove acentos (ã→a, ç→c, é→e)
 *  - espaços viram `.`
 *  - remove qualquer caractere que não seja `a-z 0-9 . _ -`
 *  - colapsa pontos consecutivos
 *
 * Usado em campos `username` para garantir um valor "limpo" que respeite os
 * requisitos comuns de login (sem espaço, sem acento, sem maiúsculas).
 */
export function slugifyUsername(raw: string): string {
  return (raw ?? '')
    .toLowerCase()
    // Normaliza decomposto (NFD) e remove os diacríticos (categoria Mn)
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
    // Espaços viram pontos
    .replace(/\s+/g, '.')
    // Permite apenas a-z 0-9 . _ -
    .replace(/[^a-z0-9._-]/g, '')
    // Colapsa pontos consecutivos
    .replace(/\.{2,}/g, '.')
}
